import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiGet, apiPost } from "@/shared/api/client";
import { queryKeys, type EstimateInput } from "@/shared/api/query-keys";
import type { BillingPeriod, CatalogueDto, CatalogueItemDto, EstimateDto } from "@/shared/api/types";
import { useOptionalSession } from "@/shared/auth/session";
import { capacityQuoteItems } from "@/shared/components/plan-configurator/capacity-plan";
import type { PlanConfiguration } from "@/shared/components/plan-configurator/plan-pricing";
import { nonZeroItems } from "@/shared/components/plan-configurator/plan-pricing";
import { CATALOGUE_STALE_MS, ESTIMATE_DEBOUNCE_MS } from "@/shared/constants/limits";
import { useDebouncedValue } from "./useDebouncedValue";

/**
 * `GET /catalogue` and `POST /catalogue/estimate` (ADR-0015 §3). Both are callable by a session with no
 * organisation, which is what lets the signup screen price a configuration before one exists; with an
 * organisation selected the api applies that organisation's own price overrides and discounts, so the
 * cache is keyed by the tenant id (null when there is none).
 */

/** The tenant the prices belong to, or null during signup. Onboarding has an account but no organisation. */
function usePriceScope(): string | null {
  return useOptionalSession()?.tenant.id ?? null;
}

/** The items on sale. Prices change rarely and only from the internal portal, so this is read once and kept. */
export function useCatalogueQuery(enabled = true) {
  const tenantId = usePriceScope();
  return useQuery({
    queryKey: queryKeys.catalogue.list(tenantId),
    queryFn: ({ signal }) => apiGet<CatalogueDto>("/catalogue", undefined, signal),
    enabled,
    staleTime: CATALOGUE_STALE_MS,
  });
}

/**
 * What a configuration costs, priced by the api rather than in the browser (M11 §3.1). Debounced, because
 * the stepper fires on every press and an estimate is a round trip; the debounced value is the query key,
 * so a configuration already priced comes straight back from the cache while a new one waits.
 *
 * `POST` is a write verb but this is a read: the estimate is explicitly documented as writing nothing, and
 * it is a POST only because a configuration does not fit in a query string.
 */
export function useEstimateQuery(config: PlanConfiguration, enabled = true) {
  const tenantId = usePriceScope();
  // Debounced as a string, not an object: `useDebouncedValue` compares by identity, and a fresh object
  // every render would restart the timer on every render — including the one its own result causes.
  const serialised = JSON.stringify({ period: config.period, items: nonZeroItems(config) });
  const debouncedJson = useDebouncedValue(serialised, ESTIMATE_DEBOUNCE_MS);
  const body = useMemo(() => JSON.parse(debouncedJson) as EstimateInput, [debouncedJson]);
  return useQuery({
    queryKey: queryKeys.catalogue.estimate(tenantId, body),
    queryFn: () => apiPost<EstimateDto>("/catalogue/estimate", body),
    enabled: enabled && body.items.length > 0,
    // The previous estimate stays on screen while the next one is priced: a total that blinks to a
    // skeleton on every press of + is unreadable, and the figures are only a few hundred ms apart.
    placeholderData: (previous) => previous,
  });
}

/**
 * One unit of EACH capacity item on sale — Access Control, Extra terminal, Extra people — priced by the api
 * for this period, so the Terminals and people card weighs its mix at what this organisation is actually
 * charged: its own price and the discount rule that covers each item. A rule may cover Extra people and not
 * Extra terminal (`item_code`), and the draft's own estimate prices only what the draft holds, so the item
 * the mix is deciding whether to buy would otherwise be weighed at its list price.
 *
 * In the same (purchase) context as `useEstimateQuery`, so the card's mix and the summary beside it read the
 * same discounts. Discount rules carry no quantity condition, so the unit price at the item's minimum
 * quantity (`capacityQuoteItems`) is the price of every unit.
 */
export function useCapacityUnitQuote(
  catalogue: readonly CatalogueItemDto[] | undefined,
  period: BillingPeriod,
  enabled = true,
) {
  const tenantId = usePriceScope();
  const items = catalogue ? capacityQuoteItems(catalogue) : [];
  const body: EstimateInput = { period, items };
  return useQuery({
    queryKey: queryKeys.catalogue.estimate(tenantId, body),
    queryFn: () => apiPost<EstimateDto>("/catalogue/estimate", body),
    enabled: enabled && items.length > 0,
    staleTime: CATALOGUE_STALE_MS,
  });
}

/**
 * What ONE line would add to the renewal invoice, ex-GST: the api's own price for it in the renewal scope
 * (`context.orderKind: "renewal"`) — the same builder, this organisation's own prices and the same
 * renewal-scope discount rules that price `GET /billing`'s `atRenewal` column, which is where the trial
 * banner's "₹3,008 is added at your renewal" comes from.
 *
 * It exists for the line that column cannot price yet: an add-on nobody holds is `not_held` there, with no
 * price on either side, until its trial has started. So the question is put to the estimate instead of
 * multiplying a catalogue price out in the browser, and the answer is the figure the banner will print the
 * moment the trial is running. `null` when the api prices no such line.
 */
export function useRenewalQuote(
  line: { code: string; quantity: number } | null,
  period: BillingPeriod,
  enabled: boolean,
) {
  const tenantId = usePriceScope();
  const body: EstimateInput = { period, items: line ? [line] : [], context: { orderKind: "renewal" } };
  return useQuery({
    queryKey: queryKeys.catalogue.estimate(tenantId, body),
    queryFn: () => apiPost<EstimateDto>("/catalogue/estimate", body),
    enabled: enabled && line !== null && line.quantity > 0,
    staleTime: CATALOGUE_STALE_MS,
    select: (estimate) => estimate.lines.find((l) => l.itemCode === line?.code)?.amountPaise ?? null,
  });
}
